import { useCallback, useEffect, useRef, useState } from "react";

/**
 * 텍스트를 클립보드에 복사하고 "복사됨" 표시를 일정 시간 유지하는 공용 훅.
 *
 * `navigator.clipboard`를 우선 사용하고, 미지원(구형 브라우저·비 HTTPS) 환경에서는
 * 임시 textarea + `document.execCommand("copy")`로 폴백한다.
 * 복사 성공 시 `copiedKey`가 세팅되고 `duration`(ms) 후 자동으로 해제된다.
 *
 * 여러 항목을 나열하는 목록에서 "어떤 행을 복사했는지"를 구분하려면 `copy(text, key)`로
 * 행 식별자를 함께 넘기고 `isCopied(key)`로 확인한다. key를 생략하면 복사한 텍스트가 key가 된다.
 *
 * @param {object} [options]
 * @param {number} [options.duration=1000] 복사됨 표시 유지 시간(ms)
 * @returns {{ copy: (text: string, key?: string) => Promise<boolean>, isCopied: (key?: string) => boolean, copiedKey: string | null }}
 */
const useCopyToClipboard = ({ duration = 1000 } = {}) => {
    const [copiedKey, setCopiedKey] = useState(null);
    const timeoutRef = useRef(null);
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, []);

    const copy = useCallback(
        async (text, key) => {
            if (!text) return false;
            try {
                if (navigator.clipboard && navigator.clipboard.writeText) {
                    await navigator.clipboard.writeText(text);
                } else {
                    const textarea = document.createElement("textarea");
                    textarea.value = text;
                    document.body.appendChild(textarea);
                    textarea.select();
                    const succeeded = document.execCommand("copy");
                    document.body.removeChild(textarea);
                    if (!succeeded) return false;
                }

                if (!isMountedRef.current) return true;

                setCopiedKey(key ?? text);
                if (timeoutRef.current) clearTimeout(timeoutRef.current);
                timeoutRef.current = setTimeout(() => {
                    setCopiedKey(null);
                }, duration);
                return true;
            } catch (error) {
                console.error("Failed to copy:", error);
                return false;
            }
        },
        [duration],
    );

    const isCopied = useCallback(
        (key) => {
            if (copiedKey === null) return false;
            return key === undefined ? true : copiedKey === key;
        },
        [copiedKey],
    );

    return { copy, isCopied, copiedKey };
};

export default useCopyToClipboard;
